import { Box, createStyles, Stack } from '@mantine/core';
import type { Message } from '@repo/api-codegen';
import { isNull } from 'lodash-es';
import { useMemo } from 'react';
import { AnimatedContent } from './AnimatedContent';
import { processContentWithEmbedded } from './utils';

import { ProposalsGroupRenderer } from './components/ProposalsGroupRenderer';
import { ThinkingSection } from './components/ThinkingSection';

const useStyles = createStyles((theme) => ({
	messageBlock: {
		width: '100%',
		wordBreak: 'break-word',
		'&:not(:last-child)': {
			marginBottom: theme.spacing.md,
		},
	},
}));

interface MessageContentV3Props {
	messages: Message[];
	isRunning: boolean;
}

export function MessageContentV3({
	messages,
	isRunning,
}: MessageContentV3Props) {
	const { classes } = useStyles();

	const messageSections = useMemo(() => {
		// Special case: when not running, all messages except final one should be thinking
		// except if the final message is `thinking: true` - user might have stopped the AI and we don't want to show duplicate thinking sections
		if (!isRunning && messages.length > 1) {
			const finalMessage = messages[messages.length - 1];

			if (finalMessage.thinking) {
				return [messages];
			}

			const thinkingMessages = messages
				.slice(0, -1)
				.map((msg) => ({ ...msg, thinking: true }));
			return [
				thinkingMessages, // All messages except last as thinking section
				[finalMessage], // Final message as normal section
			];
		}

		// Find first and last thinking message indices
		const firstThinkingIndex = messages.findIndex((msg) => msg.thinking);
		const lastThinkingIndex =
			messages.length -
			1 -
			[...messages].reverse().findIndex((msg) => msg.thinking);

		// If no thinking messages, return all messages as one normal section
		if (firstThinkingIndex === -1) {
			return [messages];
		}

		const sections: Message[][] = [];

		// Add messages before first thinking as normal section
		if (firstThinkingIndex > 0) {
			sections.push(messages.slice(0, firstThinkingIndex));
		}

		// Add all messages between first and last thinking (inclusive) as thinking section
		sections.push(messages.slice(firstThinkingIndex, lastThinkingIndex + 1));

		// Add remaining messages as normal section
		if (lastThinkingIndex < messages.length - 1) {
			sections.push(messages.slice(lastThinkingIndex + 1));
		}

		return sections;
	}, [messages, isRunning]);

	const generatedProposalGroups = useMemo(() => {
		if (isRunning) {
			return [];
		}

		const contentSegments = messages
			.map((msg) => msg.tool_response)
			.filter((response) => !isNull(response))
			.flatMap((response) =>
				processContentWithEmbedded(response?.output ?? '')
			);

		const proposalsGroupSegments = contentSegments.filter(
			(segment) => segment.type === 'proposals-group'
		);

		const proposalGroups: string[] = [];
		for (const segment of proposalsGroupSegments) {
			if (!proposalGroups.includes(segment.content)) {
				proposalGroups.push(segment.content);
			}
		}

		return proposalGroups;
	}, [messages, isRunning]);

	return (
		<Stack spacing={0}>
			{messageSections.map((sectionMessages, index) => {
				const isThinking = sectionMessages[0]?.thinking ?? false;

				if (isThinking) {
					return (
						<ThinkingSection
							key={`thinking-${index}`}
							messages={sectionMessages}
							promptRunning={isRunning}
							uniqueId={`thinking-${index}`}
						/>
					);
				}

				return sectionMessages.map((msg, msgIdx) => {
					const isLastMessage = msgIdx === sectionMessages.length - 1;
					const isTyping = isLastMessage && isRunning;

					return (
						<Box
							key={msg.content + msg.sender}
							className={classes.messageBlock}
						>
							<AnimatedContent
								cacheKey={`message-${index}-${msgIdx}`}
								content={msg.content}
								isTyping={isTyping}
							/>
						</Box>
					);
				});
			})}
			{generatedProposalGroups &&
				generatedProposalGroups.length > 0 &&
				!isRunning && (
					<Stack spacing="md">
						{generatedProposalGroups.map((group) => (
							<ProposalsGroupRenderer
								key={group}
								proposalGroup={group}
								mode={{
									type: 'inline_list',
								}}
							/>
						))}
					</Stack>
				)}
		</Stack>
	);
}
